import React, { useMemo } from "react";

const PALETTES = [
  ["#FF1A1A", "#000000", "#F5F5F5", "#B8860B"],
  ["#000000", "#FF1A1A", "#D9D9D9", "#F5F5F5"],
  ["#F5F5F5", "#000000", "#FF1A1A", "#B8860B"],
  ["#1A1A1A", "#F5F5F5", "#FF1A1A", "#D9D9D9"],
  ["#B8860B", "#000000", "#F5F5F5", "#FF1A1A"],
  ["#FF1A1A", "#F5F5F5", "#000000", "#D9D9D9"],
];

function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < String(str).length; i++) {
    h ^= String(str).charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function rng(seed) {
  let a = hash(seed);
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function GeoArt({ seed = "x", className = "", ratio = "4/5" }) {
  const art = useMemo(() => {
    const r = rng(seed);
    const pal = PALETTES[Math.floor(r() * PALETTES.length)];
    const [bg, ink, paper, accent] = pal;
    const cx = 130 + r() * 140;
    const cy = 160 + r() * 120;
    const headR = 90 + r() * 70;
    const eyeY = cy - headR * 0.25;
    const eyeDx = headR * (0.35 + r() * 0.15);
    const eyeR = headR * (0.1 + r() * 0.1);
    const mouthW = headR * (0.5 + r() * 0.4);
    const mouthY = cy + headR * (0.3 + r() * 0.25);
    const crown = r() > 0.45;
    const zig = [];
    if (crown) {
      const n = 5 + Math.floor(r() * 4);
      const w = headR * 2.2;
      for (let i = 0; i <= n; i++) {
        zig.push(`${cx - w / 2 + (w / n) * i},${cy - headR - 10 - (i % 2 === 0 ? 30 + r() * 30 : 0)}`);
      }
    }
    const dots = [];
    const dn = 6 + Math.floor(r() * 6);
    for (let i = 0; i < dn; i++) dots.push({ x: r() * 400, y: r() * 500, rr: 3 + r() * 9 });
    const bar = { x: r() * 260, y: 380 + r() * 90, w: 60 + r() * 140, h: 14 + r() * 26 };
    const arc = r() > 0.5;
    return { bg, ink, paper, accent, cx, cy, headR, eyeY, eyeDx, eyeR, mouthW, mouthY, zig, dots, bar, arc };
  }, [seed]);

  return (
    <svg viewBox="0 0 400 500" className={className} style={{ aspectRatio: ratio }} role="img" aria-label="Geometric placeholder artwork" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="500" fill={art.bg} />
      {art.dots.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r={d.rr} fill={art.paper} opacity="0.25" />
      ))}
      {art.arc && <path d={`M 20 ${art.cy + 120} A 200 200 0 0 1 380 ${art.cy + 120}`} fill="none" stroke={art.accent} strokeWidth="10" opacity="0.8" />}
      {art.zig.length > 0 && <polyline points={art.zig.join(" ")} fill="none" stroke={art.accent} strokeWidth="12" strokeLinejoin="miter" />}
      <circle cx={art.cx} cy={art.cy} r={art.headR} fill={art.ink} />
      <rect x={art.cx - art.eyeDx - art.eyeR * 1.6} y={art.eyeY - art.eyeR} width={art.eyeR * 3.2} height={art.eyeR * 2} fill={art.paper} />
      <rect x={art.cx + art.eyeDx - art.eyeR * 1.6} y={art.eyeY - art.eyeR} width={art.eyeR * 3.2} height={art.eyeR * 2} fill={art.paper} />
      <circle cx={art.cx - art.eyeDx} cy={art.eyeY} r={art.eyeR * 0.5} fill={art.bg === "#000000" ? "#F5F5F5" : "#000000"} />
      <circle cx={art.cx + art.eyeDx} cy={art.eyeY} r={art.eyeR * 0.5} fill={art.bg === "#000000" ? "#F5F5F5" : "#000000"} />
      <path d={`M ${art.cx - art.mouthW / 2} ${art.mouthY} Q ${art.cx} ${art.mouthY + art.headR * 0.35} ${art.cx + art.mouthW / 2} ${art.mouthY}`} fill="none" stroke={art.paper} strokeWidth="9" strokeLinecap="round" />
      <rect x={art.bar.x} y={art.bar.y} width={art.bar.w} height={art.bar.h} fill={art.accent} />
      <rect x={art.bar.x} y={art.bar.y - art.bar.h - 8} width={art.bar.w * 0.5} height={art.bar.h * 0.6} fill={art.paper} opacity="0.85" />
    </svg>
  );
}
